'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, Layers, Plus, TriangleAlert } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Trail } from './demoParts'

// GHL snapshots: the page's argument. A quick export of one client's account
// breaks when deployed to the next (wrong custom fields, wrong calendar,
// wrong triggers); a snapshot built for reuse deploys clean. The grid shows
// every client on the snapshot, and the visitor can deploy to another one.

const PACK = ['14 workflows', '6 forms', '3 calendars', '4 pipelines']
// The first five are already on the snapshot; Peak Plumbing is the new deploy.
const CLIENTS = ['Apex Roofing', 'Clear Dental', 'Oak Realty', 'North HVAC', 'Bright Smiles', 'Peak Plumbing', 'Metro Movers', 'Green Lawn', 'Harbor Legal', 'Pine Pest', 'Elite Auto', 'Coastal Clean']

const PATHS = {
  export: {
    trail: [
      { at: 1, text: 'Deployed to Peak Plumbing' },
      { at: 2, text: "Workflows still use the last client's custom fields", tone: 'bad' },
      { at: 3, text: 'Forms book into the wrong calendar', tone: 'bad' },
      { at: 4, text: 'Automations fire on the wrong trigger', tone: 'bad' },
      { at: 5, text: 'Hours of manual fixes before launch', tone: 'bad' },
    ],
    result: { tone: 'bad', text: 'Rework on every client' },
  },
  reuse: {
    trail: [
      { at: 1, text: 'Deployed to Peak Plumbing' },
      { at: 2, text: 'Generic custom fields map on their own' },
      { at: 3, text: "Forms connect to Peak Plumbing's own calendar" },
      { at: 4, text: 'Triggers adapt to this sub-account' },
      { at: 5, text: 'Live the same day, zero rework' },
    ],
    result: { tone: 'good', text: 'Deploys clean every time' },
  },
}
const DELAYS = [600, 1000, 1000, 1000, 1000, 700]

export default function SnapshotDeploy() {
  const [ref, onScreen] = useOnScreen()
  const [path, setPath] = useState('export')
  const [run, setRun] = useState(0)
  const [auto, setAuto] = useState(true)
  const [extra, setExtra] = useState(0)
  const data = PATHS[path]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${path}-${run}` })
  const finished = step >= DELAYS.length
  const clean = path === 'reuse'
  const count = Math.min(CLIENTS.length, 5 + (step >= 1 ? 1 : 0) + extra)

  useEffect(() => {
    if (!auto || path !== 'export' || !finished) return undefined
    const timer = setTimeout(() => { setPath('reuse'); setExtra(0) }, 1800)
    return () => clearTimeout(timer)
  }, [auto, path, finished])

  const pick = (key) => { setAuto(false); setPath(key); setRun((n) => n + 1); setExtra(0) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-snap"
      title="One snapshot, every new client"
      label="Demo: a GoHighLevel snapshot deployed to new clients, as a quick export versus built for reuse"
      tabs={[{ key: 'export', label: 'Quick export', tone: 'bad' }, { key: 'reuse', label: 'Built for reuse', tone: 'good' }]}
      activeTab={path}
      onTab={pick}
      onReplay={() => pick(path)}
    >
      <div className="svd-snap-card">
        <span className="svd-snap-icon" aria-hidden="true"><Layers size={17} /></span>
        <span>
          <strong>Home services snapshot · v3</strong>
          <span className="svd-snap-pack">{PACK.map((p) => <i key={p}>{p}</i>)}</span>
        </span>
      </div>
      <div className="svd-agent-grid">
        <Trail title="Deploying to a new client" items={data.trail} step={step} />
        <div className="svd-tiles-panel">
          <span className="svd-trail-title">Clients on this snapshot: {count}</span>
          <ul className="svd-tiles">
            <AnimatePresence initial={false}>
              {CLIENTS.slice(0, count).map((name) => (
                <motion.li key={`${path}-${name}`} className={clean ? 'is-ok' : 'is-warn'} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                  {clean ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : <TriangleAlert size={12} aria-hidden="true" />}
                  <span>{name}</span>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
          <button type="button" className="svd-btn" disabled={!finished || count >= CLIENTS.length} onClick={() => { setAuto(false); setExtra((n) => n + 1) }}>
            <Plus size={14} aria-hidden="true" /> Deploy to another client
          </button>
        </div>
      </div>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${path}-${run}`} />
    </DemoFrame>
  )
}
